import { gql } from '@apollo/client';
import { DEAL_DETAILS_FORM_DATA, TEAM_DATA } from './fragments';
import { graphql } from '../gql';

export const updateDocumentWithImmediateEmbedding = graphql(/* GraphQL */`
  mutation updateDocumentWithImmediateEmbedding($documentId: ID!, $document: Json, $title: String, $triggerImmediateEmbedding: Boolean) {
    updateDocument(documentId: $documentId, document: $document, title: $title, triggerImmediateEmbedding: $triggerImmediateEmbedding) {
      id
      lastUpdated
    }
  }
`);

export const createTeam = graphql(/* GraphQL */`
  mutation createTeam($name: String!, $partyId: ID!) {
    createTeam(name: $name, partyId: $partyId) {
      id
      name
      uuid
      party {
        id
      }
      adminUser {
        id
        email
        uuid
        fullName
      }
    }
  }
`);

// TODO: Move this to a file of its own
export const createExpressDocumentFromTemplate = graphql(/* GraphQL */ `
  mutation createExpressDocumentFromTemplate($dealId: ID, $teamId: ID!, $dealTitle: String, $documentTitle: String!, $templateId: ID!) {
    createExpressDocumentFromTemplate(dealId: $dealId, teamId: $teamId, dealTitle: $dealTitle, documentTitle: $documentTitle, templateId: $templateId) {
      id
      deal {
        id
      }
      team {
        id
      }
    }
  }
`);

export const updateOrganizationRequestExpiry = graphql(/* GraphQL */`
  mutation updateOrganizationRequestExpiry($organizationRequestId: ID!,   $expiresAt: String!) {
    updateOrganizationRequestExpiry(organizationRequestId: $organizationRequestId, expiresAt: $expiresAt) {
      id
      expiresAt
    }
  }
`);

export const createDeal = graphql(/* GraphQL */`
  mutation createDeal($teamId: ID!, $title: String!, $folderId: ID) {
    createDeal(teamId: $teamId, title: $title, folderId: $folderId) {
      id
      title
    }
  }
`);

export const regenerateDocumentPdf = graphql(/* GraphQL */`
  mutation regenerateDocumentPdf($documentId: ID!) {
    regenerateDocumentPdf(documentId: $documentId) {
      id
      generatedDocumentPdfUrl
      lastUpdated
    }
  }
`);

/** Capture a full-body version snapshot. Polymorphic: serves BOTH deal
 *  documents ("Document") and templates ("DocumentTemplate"). */
export const saveDocumentVersion = graphql(/* GraphQL */`
  mutation saveDocumentVersion($documentId: ID!, $documentType: String!, $name: String, $event: String) {
    saveDocumentVersion(documentId: $documentId, documentType: $documentType, name: $name, event: $event) {
      revision {
        id
        revisionId
        name
        hasSnapshot
        snapshotFormat
        createdAt
      }
      errors
    }
  }
`);

/** Rename a saved version (sets data.name). Polymorphic — deals + templates. */
export const renameDocumentVersion = graphql(/* GraphQL */`
  mutation renameDocumentVersion($revisionId: ID!, $name: String!) {
    renameDocumentVersion(revisionId: $revisionId, name: $name) {
      revision { id name }
      errors
    }
  }
`);

export const exportDocumentPdf = graphql(/* GraphQL */`
  mutation exportDocumentPdf($documentId: ID!, $selectedAttachmentIds: [ID!]) {
    exportDocumentPdf(documentId: $documentId, selectedAttachmentIds: $selectedAttachmentIds) {
      queued
      errors
    }
  }
`);

export const exportTemplatePdf = gql`
  mutation exportTemplatePdf($documentTemplateId: ID!) {
    exportTemplatePdf(documentTemplateId: $documentTemplateId) {
      queued
      errors
    }
  }
`;

export const updateDealDetails = gql`
  mutation updateDealDetails($dealId: ID!, $title: String, $dealDetails: DealDetailsType) {
    updateDealDetails(dealId: $dealId, title: $title, dealDetails: $dealDetails) {
      ...DealDetailsFormData
    }
  }
  ${DEAL_DETAILS_FORM_DATA}
`;

// Deal Folder Mutations
export const createDealFolder = graphql(/* GraphQL */`
  mutation createDealFolder($name: String!, $teamId: ID!, $parentFolderId: ID) {
    createDealFolder(name: $name, teamId: $teamId, parentFolderId: $parentFolderId) {
      id
      name
    }
  }
`);

export const updateDealFolder = graphql(/* GraphQL */`
  mutation updateDealFolder($id: ID!, $name: String!) {
    updateDealFolder(id: $id, name: $name) {
      id
      name
    }
  }
`);

export const deleteDealFolder = graphql(/* GraphQL */`
  mutation deleteDealFolder($id: ID!, $force: Boolean) {
    deleteDealFolder(id: $id, force: $force)
  }
`);

export const moveDealToFolder = graphql(/* GraphQL */`
  mutation moveDealToFolder($dealId: ID!, $folderId: ID) {
    moveDealToFolder(dealId: $dealId, folderId: $folderId) {
      id
      folder {
        id
        name
      }
    }
  }
`);

export const moveDealFolder = graphql(/* GraphQL */`
  mutation moveDealFolder($folderId: ID!, $parentFolderId: ID) {
    moveDealFolder(folderId: $folderId, parentFolderId: $parentFolderId) {
      id
      name
      parentFolder {
        id
        name
      }
    }
  }
`);

export const updateTeam = graphql(/* GraphQL */`
  mutation updateTeam($id: ID!, $name: String) {
    updateTeam(id: $id, name: $name) {
      id
      name
    }
  }
`);

export const updateUserPermissions = graphql(/* GraphQL */`
  mutation updateUserPermissions($userId: ID!, $teamId: ID!, $permissionUpdates: [PermissionUpdateType!]!) {
    updateUserPermissions(userId: $userId, teamId: $teamId, permissionUpdates: $permissionUpdates) {
      user {
        id
        teamPermissionsByTeam(teamId: $teamId) {
          id
          allowed
          permission {
            id
            displayOrder
            key
            name
            description
          }
        }
      }
    }
  }
`);

export const removeTeamMemberFromTeam = gql`
  mutation removeTeamMemberFromTeam($userId: ID!, $teamId: ID!) {
    removeTeamMemberFromTeam(userId: $userId, teamId: $teamId) {
      ...TeamData
    }
  }
  ${TEAM_DATA}
`;

export const removeTeamMemberFromDeal = graphql(/* GraphQL */`
  mutation removeTeamMemberFromDeal($userId: ID!, $dealId: ID!) {
    removeTeamMemberFromDeal(userId: $userId, dealId: $dealId) {
      id
      dealAccessUsers
    }
  }
`);

export const inviteNewTeamMembersByEmail = gql`
  mutation inviteNewTeamMembersByEmail(
    $teamId: ID!
    $emails: [String!]!
    $userPermissions: [UserPermissionsInputType!]!
    $defaultDeals: [ID!]
    $additionalMessage: String
  ) {
    inviteNewTeamMembersByEmail(
      teamId: $teamId
      emails: $emails
      userPermissions: $userPermissions
      defaultDeals: $defaultDeals
      additionalMessage: $additionalMessage
    ) {
      ...TeamData
    }
  }
  ${TEAM_DATA}
`;

export const replaceImportDocument = graphql(/* GraphQL */`
  mutation replaceImportedDocument(
    $documentId: ID!
    $fileData: BinaryFileInputType!
    $teamId: ID!
  ) {
    replaceImportedDocument(
      documentId: $documentId
      fileData: $fileData
      teamId: $teamId
    ) {
      document
    }
  }
`);

export const attachGeneratedPdf = graphql(/* GraphQL */`
  mutation attachGeneratedPdf(
    $documentId: ID!
    $fileData: BinaryFileInputType!
    $teamId: ID!
  ) {
    attachGeneratedPdf(
      documentId: $documentId
      fileData: $fileData
      teamId: $teamId
    ) {
      id
      lastUpdated
    }
  }
`);

export const replaceImportDocumentTemplate = graphql(/* GraphQL */ `
  mutation replaceImportedDocumentTemplate(
    $templateId: ID!
    $fileData: BinaryFileInputType!
    $teamId: ID!
  ) {
    replaceImportedDocumentTemplate(
      templateId: $templateId
      fileData: $fileData
      teamId: $teamId
    ) {
      template
    }
  }
`);

export const createDocumentTemplate = graphql(/* GraphQL */`
  mutation createDocumentTemplate(
    $teamId: ID!
    $name: String!
    $templateType: String!
    $fileData: BinaryFileInputType
    $filesData: [BinaryFileInputType!]
    $restrictedEditing: Boolean
  ) {
    createDocumentTemplate(
      teamId: $teamId
      name: $name
      templateType: $templateType
      restrictedEditing: $restrictedEditing
      fileData: $fileData
      filesData: $filesData
    ) {
      id
      name
      template
      team {
        id
      }
      editorConfiguration
    }
  }
`);

export const convertDocxToDocumentTemplate = graphql(/* GraphQL */`
  mutation convertDocxToDocumentTemplate(
    $teamId: ID!
    $name: String!
    $templateType: String!
    $fileData: BinaryFileInputType!
    $restrictedEditing: Boolean
  ) {
    convertDocxToDocumentTemplate(
      teamId: $teamId
      name: $name
      templateType: $templateType
      restrictedEditing: $restrictedEditing
      fileData: $fileData
    ) {
      id
      name
      template
      team {
        id
      }
      editorConfiguration
    }
  }
`);

export const createImportedDocument = graphql(/* GraphQL */`
  mutation createImportedDocument(
    $dealId: ID!
    $teamId: ID!
    $title: String!
    $fileData: BinaryFileInputType
    $filesData: [BinaryFileInputType!]
  ) {
    createImportedDocument(
      dealId: $dealId
      teamId: $teamId
      title: $title
      fileData: $fileData
      filesData: $filesData
    ) {
      id
      status
      currentTeamPossession
      title
      lastUpdated
      eversignPdfUrl
      shouldReturnForReceiverReview
      source
      importedAssetUrl
      importedAssetContentType
      generatedDocumentPdfUrl
    }
  }
`);

export const convertDocxToDocument = graphql(/* GraphQL */`
  mutation convertDocxToDocument(
    $dealId: ID!
    $teamId: ID!
    $title: String!
    $fileData: BinaryFileInputType!
    $configuration: EditorConfigurationInputType
  ) {
    convertDocxToDocument(
      dealId: $dealId
      teamId: $teamId
      title: $title
      fileData: $fileData
      configuration: $configuration
    ) {
      id
      status
      currentTeamPossession
      title
      lastUpdated
      eversignPdfUrl
      shouldReturnForReceiverReview
      source
      importedAssetUrl
      importedAssetContentType
      generatedDocumentPdfUrl
    }
  }
`);


export const updateDocumentTemplate = graphql(/* GraphQL */`
  mutation updateDocumentTemplate($documentTemplateId: ID!, $name: String, $template: Json, $hidden: Boolean, $folderId: ID) {
    updateDocumentTemplate(
      documentTemplateId: $documentTemplateId
      name: $name
      hidden: $hidden
      template: $template
      folderId: $folderId
    ) {
      id
      name
      template
      updatedAt
      team {
        id
      }
      folder {
        id
        name
        path
      }
    }
  }
`);

export const resendInvitation = graphql(/* GraphQL */`
  mutation resendInvitation($invitationId: ID!) {
    resendInvitation(invitationId: $invitationId) {
      success
    }
  }
`);

export const cancelInvitation = graphql(/* GraphQL */`
  mutation cancelInvitation($invitationId: ID!) {
    revokeInvitation(invitationId: $invitationId) {
      id
      status
    }
  }
`);

export const updatePendingInvitation = graphql(/* GraphQL */`
    mutation updatePendingInvitation($id: ID!, $permissionIds: [ID!]!, $dealIds: [ID!]!) {
      updatePendingInvitation(id: $id, permissionIds: $permissionIds, dealIds: $dealIds) {
        id
        status
      }
    }
`);

export const updateUserPassword = graphql(/* GraphQL */`
  mutation updateUserPassword($password: String!, $passwordConfirmation: String!, $currentPassword: String!) {
    updateUserPassword(
      password: $password
      passwordConfirmation: $passwordConfirmation
      currentPassword: $currentPassword
    ) {
      success
    }
  }
`);

export const updateUser = graphql(/* GraphQL */`
  mutation updateUser($fullName: String, $companyName: String, $email: String, $currentPassword: String, $cancelEmailChange: Boolean) {
    updateUser(fullName: $fullName, companyName: $companyName, email: $email, currentPassword: $currentPassword, cancelEmailChange: $cancelEmailChange) {
      currentUser {
        id
        fullName
        companyName
        email
        unconfirmedEmail
      }
      success
      emailChangeRequested
    }
  }
`);

export const cloneDocumentTemplate = graphql(/* GraphQL */`
  mutation cloneDocumentTemplate(
    $documentTemplateId: ID!
    $targetTeamId: ID
    $name: String
    $folderId: ID
    $restrictedEditing: Boolean
  ) {
    cloneDocumentTemplate(
      documentTemplateId: $documentTemplateId
      targetTeamId: $targetTeamId
      name: $name
      folderId: $folderId
      restrictedEditing: $restrictedEditing
    ) {
      id
      name
      country
      state
      template
      hidden
      team {
        id
        name
      }
      folder {
        id
        name
      }
    }
  }
`);

export const shareTemplateToWorkspaces = graphql(/* GraphQL */`
  mutation shareTemplateToWorkspaces($templateId: ID!) {
    shareTemplateToWorkspaces(templateId: $templateId) {
      documentTemplate {
        id
        sharedToAffiliatedWorkspaces
        sharedFromTeam {
          id
          name
        }
      }
      isShared
      affiliatedWorkspaceCount
    }
  }
`);

export const createDocumentImage = graphql(/* GraphQL */`
  mutation createDocumentImage($documentId: ID!, $fileData: BinaryFileInputType!) {
    createDocumentImage(documentId: $documentId, fileData: $fileData) {
      success
      path
    }
  }
`);

export const createDocumentTemplateImage = graphql(/* GraphQL */`
  mutation createDocumentTemplateImage($documentTemplateId: ID!, $fileData: BinaryFileInputType!) {
    createDocumentTemplateImage(documentTemplateId: $documentTemplateId, fileData: $fileData) {
      success
      path
    }
  }
`);

export const lockDocument = graphql(/* GraphQL */`
  mutation lockDocument($documentId: ID!, $userId: ID!) {
    lockDocument(documentId: $documentId, userId: $userId) {
      documentId
      userId
      type
      lockedAt
    }
  }
`);

export const unlockDocument = graphql(/* GraphQL */`
  mutation unlockDocument($documentId: ID!, $userId: ID!, $sessionActivities: [String]!) {
    unlockDocument(documentId: $documentId, userId: $userId, sessionActivities: $sessionActivities) {
      documentId
      userId
      type
      lockedAt
    }
  }
`);

export const lockDocumentTemplate = graphql(/* GraphQL */`
  mutation lockDocumentTemplate($documentId: ID!, $userId: ID!) {
    lockDocumentTemplate(documentId: $documentId, userId: $userId) {
      documentId
      userId
      type
      lockedAt
    }
  }
`);

export const unlockDocumentTemplate = graphql(/* GraphQL */`
  mutation unlockDocumentTemplate($documentId: ID!, $userId: ID!) {
    unlockDocumentTemplate(documentId: $documentId, userId: $userId) {
      documentId
      userId
      type
      lockedAt
    }
  }
`);

export const createDocumentPlaceholder = graphql(/* GraphQL */`
  mutation createDocumentPlaceholder(
    $isRequired: Boolean!,
    $linkableId: ID!,
    $linkableType: String!,
    $teamId: ID!,
    $title: String!
  ) {
    createDocumentPlaceholder(
      isRequired: $isRequired,
      linkableId: $linkableId,
      linkableType: $linkableType,
      teamId: $teamId,
      title: $title
    ) {
      id
      createdAt
      isRequired
      status
      team {
        id
      }
      title
      updatedAt
      uuid
    }
  }
`);

export const removeDocumentPlaceholder = graphql(/* GraphQL */`
  mutation removeDocumentPlaceholder(
    $linkableId: ID!,
    $linkableType: String!,
    $placeholderUuid: ID!,
    $teamId: ID!
  ) {
    removeDocumentPlaceholder(
      linkableId: $linkableId,
      linkableType: $linkableType,
      placeholderUuid: $placeholderUuid,
      teamId: $teamId
    ) {
      success
    }
  }
`);

export const updateDocumentPlaceholder = graphql(/* GraphQL */`
  mutation updateDocumentPlaceholder(
    $isRequired: Boolean,
    $linkableId: ID!,
    $linkableType: String!,
    $placeholderUuid: ID!,
    $teamId: ID!,
    $status: String,
    $title: String,
    $currentValue: String,
    $dealFieldGroupId: ID,
    $valueHistory: JSON
  ) {
    updateDocumentPlaceholder(
      isRequired: $isRequired,
      linkableId: $linkableId,
      linkableType: $linkableType,
      placeholderUuid: $placeholderUuid,
      teamId: $teamId,
      status: $status,
      title: $title,
      currentValue: $currentValue,
      dealFieldGroupId: $dealFieldGroupId,
      valueHistory: $valueHistory
    ) {
      id
      createdAt
      isRequired
      status
      team {
        id
      }
      title
      updatedAt
      uuid
    }
  }
`);

export const createDocumentSuggestion = graphql(/* GraphQL */`
  mutation createDocumentSuggestion(
    $documentId: ID!
    $documentType: String!
    $type: String!
    $data: Json!
    $attributes: Json!
    $externalId: ID!
    $originalSuggestionExternalId: ID
  ) {
    createDocumentSuggestion(
      documentId: $documentId
      documentType: $documentType
      type: $type
      data: $data
      attributes: $attributes
      originalSuggestionExternalId: $originalSuggestionExternalId
      externalId: $externalId
    ) {
      createdAt
      authorRole
    }
  }
`);

export const syncDocumentSuggestions = graphql(/* GraphQL */`
  mutation syncDocumentSuggestions($documentId: ID!, $documentType: String!, $externalIds: [ID!]!) {
    syncDocumentSuggestions(documentId: $documentId, documentType: $documentType, externalIds: $externalIds) {
      id
    }
  }
`);

export const deleteDocumentSuggestionBySuggestionId = graphql(/* GraphQL */`
  mutation deleteDocumentSuggestionBySuggestionId($documentSuggestionExternalId: ID!) {
    deleteDocumentSuggestion(documentSuggestionExternalId: $documentSuggestionExternalId) {
      success
    }
  }
`);

export const acceptDocumentSuggestionBySuggestionId = graphql(/* GraphQL */`
  mutation acceptDocumentSuggestionBySuggestionId($documentSuggestionExternalId: ID!, $data: Json!, $attributes: Json!) {
    acceptDocumentSuggestion(
      documentSuggestionExternalId: $documentSuggestionExternalId
      data: $data
      attributes: $attributes
    ) {
      success
    }
  }
`);

export const rejectDocumentSuggestionBySuggestionId = graphql(/* GraphQL */`
  mutation rejectDocumentSuggestionBySuggestionId($documentSuggestionExternalId: ID!, $data: Json!, $attributes: Json!) {
    rejectDocumentSuggestion(
      documentSuggestionExternalId: $documentSuggestionExternalId
      data: $data
      attributes: $attributes
    ) {
      success
    }
  }
`);

export const pendDocumentSuggestionBySuggestionId = graphql(/* GraphQL */`
  mutation pendDocumentSuggestionBySuggestionId($documentSuggestionExternalId: ID!) {
    pendDocumentSuggestion(documentSuggestionExternalId: $documentSuggestionExternalId) {
      success
    }
  }
`);

export const updateDocumentRevisions = graphql(/* GraphQL */`
  mutation updateDocumentRevisions($documentId: ID!, $documentType: String!, $data: [CkeRevisionInputType!]!) {
    updateDocumentRevisions(documentId: $documentId, documentType: $documentType, data: $data) {
      createdAt
      id
      revisionId
      attributes
      name
      creatorId
      toVersion
      fromVersion
      data
      diffData
    }
  }
`);

export const createDocumentSigner = graphql(/* GraphQL */`
  mutation CreateDocumentSigner($documentId: ID!, $email: String!, $order: Int!) {
    createSigner(documentId: $documentId, email: $email, order: $order) {
      id
      email
      order
    }
  }
`);

export const deleteDocumentSigner = graphql(/* GraphQL */`
  mutation DeleteDocumentSigner($signerId: ID!) {
    deleteSigner(signerId: $signerId) {
      id
      order
    }
  }
`);

export const updateDocumentCopyRecipients = graphql(/* GraphQL */`
  mutation UpdateDocumentCopyRecipientsList($documentId: ID!, $recipients: [String]!) {
    updateDocumentCopyRecipientsList(documentId: $documentId, recipients: $recipients) {
      id
      ccRecipients
    }
  }
`);

export const updateDocumentSigner = graphql(/* GraphQL */`
  mutation UpdateDocumentSigner($signerId: ID!, $email: String!) {
    updateSigner(signerId: $signerId, email: $email) {
      id
      email
    }
  }
`);

export const bulkUpdateSigners = graphql(/* GraphQL */`
  mutation BulkUpdateSigners($documentId: ID!, $signers: [SignerInputType!]!) {
    bulkUpdateSigners(documentId: $documentId, signers: $signers) {
      id
      email
      order
    }
  }
`);

export const updateSignersDuringSigning = graphql(/* GraphQL */`
  mutation UpdateSignersDuringSigning($documentId: ID!, $signers: [SignerInputType!]!) {
    updateSignersDuringSigning(documentId: $documentId, signers: $signers) {
      id
      email
      order
      signedAt
    }
  }
`);

export const prepareToSignDocument = graphql(/* GraphQL */`
  mutation PrepareToSignDocument($documentId: ID!, $additionalMessage: String) {
    prepareToSignDocument(documentId: $documentId, additionalMessage: $additionalMessage, confirm: true) {
      id
      title
      status
    }
  }
`);

export const approveDocumentReview = graphql(/* GraphQL */`
  mutation ApproveDocumentReview($reviewId: ID!, $reason: String) {
    approveDocumentReview(reviewId: $reviewId, reason: $reason) {
      id
      approvedAt
    }
  }
`);

export const rejectDocumentReview = graphql(/* GraphQL */`
  mutation RejectDocumentReview($reviewId: ID!, $reason: String) {
    rejectDocumentReview(reviewId: $reviewId, reason: $reason) {
      id
      rejectedAt
    }
  }
`);

export const cancelDocumentApprovalRequest = graphql(/* GraphQL */`
  mutation CancelDocumentApprovalRequest($requestId: ID!, $additionalMessage: String) {
    cancelDocumentApprovalRequest(requestId: $requestId, additionalMessage: $additionalMessage) {
      id
    }
  }
`);

export const unlockDocumentAfterApproval = graphql(/* GraphQL */`
  mutation UnlockDocumentAfterApproval($documentId: ID!, $teamId: ID!) {
    unlockDocumentAfterApproval(documentId: $documentId, teamId: $teamId) {
      id
    }
  }
`);

export const updateTemplatePdfWithFormValues = graphql(/* GraphQL */`
  mutation UpdateTemplatePdfWithFormValues($templateId: ID!, $pdfData: String!) {
    updateTemplatePdfWithFormValues(templateId: $templateId, pdfData: $pdfData) {
      id
      updatedAt
      importedAssetUrl
    }
  }
`);

// PDF.js Template Mutations (replacing XFDF)
export const saveTemplatePdfFormFields = graphql(/* GraphQL */`
  mutation SaveTemplatePdfFormFields($documentTemplateId: ID!, $fields: [PdfFormFieldInput!]!) {
    saveTemplatePdfFormFields(documentTemplateId: $documentTemplateId, fields: $fields) {
      id
      pdfFormFields
      updatedAt
    }
  }
`);

export const saveTemplatePdfAnnotations = graphql(/* GraphQL */`
  mutation SaveTemplatePdfAnnotations($documentTemplateId: ID!, $annotations: [PdfAnnotationInput!]!) {
    saveTemplatePdfAnnotations(documentTemplateId: $documentTemplateId, annotations: $annotations) {
      id
      pdfAnnotations
      updatedAt
    }
  }
`);

export const updateDocumentPdfWithFormValues = graphql(/* GraphQL */`
  mutation UpdateDocumentPdfWithFormValues($documentId: ID!, $pdfData: String!) {
    updateDocumentPdfWithFormValues(documentId: $documentId, pdfData: $pdfData) {
      id
      lastUpdated
      importedAssetUrl
    }
  }
`);

export const cancelPreparingToSignDocument = graphql(/* GraphQL */`
  mutation CancelPreparingToSignDocument($documentId: ID!, $additionalMessage: String) {
    cancelPreparingToSignDocument(documentId: $documentId, additionalMessage: $additionalMessage) {
      id
      status
      document
      title
      lastUpdated
      eversignStatus
      receiverTeam {
        id
      }
      team {
        id
      }
      deal {
        id
      }
    }
  }
`);

export const createOrganizationRequest = graphql(/* GraphQL */`
  mutation CreateOrganizationRequest(
    $organizationId: ID!
    $requesterId: ID!
    $requesterType: String!
    $membershipId: String
  ) {
    createOrganizationRequest(
      organizationId: $organizationId
      requesterId: $requesterId
      requesterType: $requesterType
      membershipId: $membershipId
    ) {
      id
    }
  }
`);

export const approveOrganizationRequest = graphql(/* GraphQL */`
  mutation approveOrganizationRequest($organizationRequestId: ID!) {
    approveOrganizationRequest(organizationRequestId: $organizationRequestId) {
      id
    }
  }
`);

export const rejectOrganizationRequest = graphql(/* GraphQL */`
  mutation rejectOrganizationRequest($organizationRequestId: ID!) {
    rejectOrganizationRequest(organizationRequestId: $organizationRequestId) {
      id
    }
  }
`);

export const removeOrganizationRequest = graphql(/* GraphQL */`
  mutation removeOrganizationRequest($organizationRequestId: ID!) {
    removeOrganizationRequest(organizationRequestId: $organizationRequestId) {
      success
    }
  }
`);

// Unified counterparty permission level mutation
export const setCounterpartyPermissionLevel = graphql(/* GraphQL */`
  mutation setCounterpartyPermissionLevel($documentId: ID!, $permissionLevel: CounterpartyPermissionLevel!) {
    setCounterpartyPermissionLevel(documentId: $documentId, permissionLevel: $permissionLevel) {
      id
      counterpartyPermissionLevel
      receiverCollaborationEnabled
      receiverPagePdfEditingEnabled
    }
  }
`);

/**
 * Convert one record (and its versions) to .docx on first open.
 *
 * The bulk rake handles everything known ahead of time; this is the long tail.
 * Without it, an unmigrated document only RENDERS — the browser fallback carries
 * no migration context, so comments and suggestions are absent and nothing is
 * persisted. Idempotent, so an already-migrated record just reports success.
 */
export const migrateRecordToDocx = graphql(/* GraphQL */`
    mutation migrateRecordToDocx($documentId: ID!, $documentType: String!) {
      migrateRecordToDocx(documentId: $documentId, documentType: $documentType) {
        migrated
        didWork
        revisionsMigrated
        errors
      }
    }
`);

export const updateDocumentEditorConfiguration = graphql(/* GraphQL */`
    mutation updateDocumentEditorConfiguration($documentId: ID!, $configuration: EditorConfigurationInputType!) {
      updateDocumentEditorConfiguration(documentId: $documentId, configuration: $configuration) {
        id
        editorConfiguration
      }
    }
`);

export const updateDocumentUseSignerOrder = graphql(/* GraphQL */`
  mutation updateDocumentUseSignerOrder($documentId: ID!, $useSignerOrder: Boolean!) {
    updateDocument(documentId: $documentId, useSignerOrder: $useSignerOrder) {
      id
      useSignerOrder
    }
  }
`);

export const updateTemplateEditorConfiguration = graphql(/* GraphQL */`
  mutation updateTemplateEditorConfiguration($templateId: ID!, $configuration: EditorConfigurationInputType!) {
    updateTemplateEditorConfiguration(templateId: $templateId, configuration: $configuration) {
      id
      editorConfiguration
    }
  }
`);

export const updateUserCommunicationPreference = graphql(/* GraphQL */`
  mutation updateUserCommunicationPreference($preferences: CommunicationPreferencesInputType!) {
    updateUserCommunicationPreference(preferences: $preferences) {
      communicationPreferences
    }
  }
`);


export const uploadDocumentExtraAttachments = graphql(/* GraphQL */`
  mutation uploadDocumentExtraAttachments(
    $documentId: ID!
    $extraAttachments: [ExtraAttachmentInputType!]!
  ) {
    uploadDocumentExtraAttachments(
      documentId: $documentId
      extraAttachments: $extraAttachments
    ) {
      extraAttachments {
        id
        attachment
        order
        linkedDocument {
          id
          title
          status
        }
        user {
          id
          email
        }
        createdAt
        updatedAt
      }
    }
  }
`);

export const removeDocumentExtraAttachment = graphql(/* GraphQL */`
  mutation removeDocumentExtraAttachment(
    $documentExtraAttachmentId: ID!
  ) {
    removeDocumentExtraAttachment(
      documentExtraAttachmentId: $documentExtraAttachmentId
    ) {
      extraAttachments {
        id
        attachment
        order
        linkedDocument {
          id
          title
          status
        }
        user {
          id
          email
        }
        createdAt
        updatedAt
      }
    }
  }
`);

export const reorderDocumentExtraAttachments = graphql(/* GraphQL */`
  mutation reorderDocumentExtraAttachments(
    $documentId: ID!
    $orderedExtraAttachmentIds: [ID!]!
  ) {
    reorderDocumentExtraAttachments(
      documentId: $documentId
      orderedExtraAttachmentIds: $orderedExtraAttachmentIds
    ) {
      extraAttachments {
        id
        attachment
        order
        linkedDocument {
          id
          title
          status
        }
        user {
          id
          email
        }
        createdAt
        updatedAt
      }
    }
  }
`);

export const linkDealDocumentAttachment = graphql(/* GraphQL */`
  mutation linkDealDocumentAttachment(
    $targetDocumentId: ID!
    $sourceDocumentIds: [ID!]!
  ) {
    linkDealDocumentAttachment(
      targetDocumentId: $targetDocumentId
      sourceDocumentIds: $sourceDocumentIds
    ) {
      extraAttachments {
        id
        attachment
        order
        linkedDocument {
          id
          title
          status
        }
        user {
          id
          email
        }
        createdAt
        updatedAt
      }
    }
  }
`);

// Template Folder Mutations
export const createTemplateFolder = graphql(/* GraphQL */`
  mutation createTemplateFolder($name: String!, $teamId: ID!, $parentFolderId: ID, $folderType: String) {
    createTemplateFolder(name: $name, teamId: $teamId, parentFolderId: $parentFolderId, folderType: $folderType) {
      id
      name
      folderType
      createdAt
      updatedAt
      team {
        id
        name
      }
      parentFolder {
        id
        name
      }
      childFolders {
        id
        name
      }
    }
  }
`);

export const updateTemplateFolder = graphql(/* GraphQL */`
  mutation updateTemplateFolder($id: ID!, $name: String, $parentFolderId: ID) {
    updateTemplateFolder(id: $id, name: $name, parentFolderId: $parentFolderId) {
      id
      name
      updatedAt
      team {
        id
        name
      }
      parentFolder {
        id
        name
      }
      childFolders {
        id
        name
      }
    }
  }
`);

export const deleteTemplateFolder = graphql(/* GraphQL */`
  mutation deleteTemplateFolder($id: ID!, $force: Boolean) {
    deleteTemplateFolder(id: $id, force: $force)
  }
`);

export const moveTemplateToFolder = graphql(/* GraphQL */`
  mutation moveTemplateToFolder($templateId: ID!, $folderId: ID) {
    moveTemplateToFolder(templateId: $templateId, folderId: $folderId) {
      id
      name
      folder {
        id
        name
      }
      updatedAt
    }
  }
`);

export const publishTemplateChanges = graphql(/* GraphQL */`
  mutation publishTemplateChanges(
    $documentTemplateId: ID!, 
    $cleanContent: Json
  ) {
    publishTemplateChanges(
      documentTemplateId: $documentTemplateId,
      cleanContent: $cleanContent
    ) {
      documentTemplate {
        id
        hasPendingChanges
        canPublish
        draftUpdatedAt
        updatedAt
      }
      errors
    }
  }
`);

export const sendMentionNotification = graphql(/* GraphQL */`
  mutation sendMentionNotification($documentId: ID!, $mentionedUserEmail: String!) {
    sendMentionNotification(documentId: $documentId, mentionedUserEmail: $mentionedUserEmail)
  }
`);

export const ensureStripeSetup = graphql(/* GraphQL */`
  mutation ensureStripeSetup {
    ensureStripeSetup {
      portalUrl
      setupCreated
    }
  }
`);

export const extractDocumentData = graphql(/* GraphQL */`
  mutation extractDocumentData($documentId: ID!) {
    extractDocumentData(documentId: $documentId) {
      id
      lastUpdated
    }
  }
`);

export const updateExtractedDocumentTerm = graphql(/* GraphQL */`
  mutation updateExtractedDocumentTerm($id: ID!, $termsData: JSON!) {
    updateExtractedDocumentTerm(id: $id, termsData: $termsData) {
      id
      termsData
      userEditedFields
      updatedByUserId
    }
  }
`);

// Amendment Linking Mutations
export const LINK_AMENDMENT_TO_BASE = gql`
  mutation linkAmendmentToBase($amendmentId: ID!, $baseDocumentId: ID!) {
    linkAmendmentToBase(amendmentId: $amendmentId, baseDocumentId: $baseDocumentId) {
      amendment {
        id
        linkStatus
        anchorDocument {
          id
          title
        }
      }
      baseDocument {
        id
        title
      }
      success
    }
  }
`;

export const UNLINK_AMENDMENT = gql`
  mutation unlinkAmendment($amendmentId: ID!) {
    unlinkAmendment(amendmentId: $amendmentId) {
      amendment {
        id
        linkStatus
        anchorDocument {
          id
          title
        }
      }
      success
    }
  }
`;

export const GET_BASE_DOCUMENTS_IN_DEAL = gql`
  query getBaseDocumentsInDeal($dealId: ID!) {
    baseDocumentsInDeal(dealId: $dealId) {
      id
      title
    }
  }
`;

export const createDocumentAbstract = graphql(/* GraphQL */`
  mutation CreateDocumentAbstract($documentId: ID!, $format: String!) {
    createDocumentAbstract(documentId: $documentId, format: $format) {
      success
      message
    }
  }
`);

export const SaveFieldPlacementsMutation = graphql(/* GraphQL */`
  mutation SaveFieldPlacements($documentId: ID!, $fieldPlacements: [FieldPlacementInput!]!) {
    saveFieldPlacements(documentId: $documentId, fieldPlacements: $fieldPlacements) {
      id
      fieldPlacements {
        id
        type
        page
        x
        y
        w
        h
        signer
        req
        data
      }
    }
  }
`);

export const SavePdfFormFieldsMutation = graphql(/* GraphQL */`
  mutation SavePdfFormFields($documentId: ID!, $fields: [PdfFormFieldInput!]!) {
    savePdfFormFields(documentId: $documentId, fields: $fields) {
      id
      pdfFormFields
      lastUpdated
    }
  }
`);

export const SavePdfAnnotationsMutation = graphql(/* GraphQL */`
  mutation SavePdfAnnotations($documentId: ID!, $annotations: [PdfAnnotationInput!]!) {
    savePdfAnnotations(documentId: $documentId, annotations: $annotations) {
      id
      pdfAnnotations
      lastUpdated
    }
  }
`);
